import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { dms, niceDate } from "./api";
import { PageHeader } from "./OwnerPages";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Store, Handshake, MapPin, LogIn, LogOut, Clock, Route as RouteIcon, RefreshCw, Calendar, Play, Pause, AlertTriangle } from "lucide-react";

// ── Fix default marker icons (leaflet + webpack) ────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom icons using divIcon (color coded)
const makeIcon = (color, glyph) => L.divIcon({
  className: "",
  html: `<div style="background:${color};width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;">
    <span style="transform:rotate(45deg);color:white;font-size:12px;font-weight:700;">${glyph}</span>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const ICON_DIST = makeIcon("#0f766e", "D");     // teal
const ICON_RET  = makeIcon("#f59e0b", "R");     // amber
const ICON_SP   = makeIcon("#e11d48", "S");     // rose
const ICON_SP_OFF = makeIcon("#64748b", "S");   // slate for offline
const ICON_TL   = makeIcon("#6366f1", "T");     // indigo — Team Leader (ASM)
const ICON_TL_OFF = makeIcon("#94a3b8", "T");   // slate for offline TL

// Pin that shows the person's first-name initial (e.g. Smit → "S", Uday → "U")
const initialOf = (name) => ((name || "?").trim().charAt(0) || "?").toUpperCase();
// Cached: a new icon object on every refresh would make Leaflet rebuild the marker.
const iconCache = {};
const makeInitialIcon = (color, name) => {
  const key = `${color}:${initialOf(name)}`;
  if (!iconCache[key]) iconCache[key] = makeIcon(color, initialOf(name));
  return iconCache[key];
};

const POLL_MS = 30000;          // positions, status and new route points
const FULL_REFRESH_MS = 300000; // shops and the staff list change rarely
const DETAIL_REFRESH_MS = 120000; // km, hours and visits of the selected person
const MAX_ROUTE_ACC_M = 100;    // same limit as the server's km calculation

const utcToday = () => new Date().toISOString().slice(0, 10);
const goodPoints = (pts) => (pts || []).filter(p => !p.stale && (p.accuracy == null || p.accuracy <= MAX_ROUTE_ACC_M));
const applyPeople = (data, people) => {
  const byId = new Map((people || []).map(p => [p.id, p]));
  const merge = (list) => (list || []).map(x => (byId.has(x.id) ? { ...x, ...byId.get(x.id) } : x));
  return {
    ...data,
    salespersons: merge(data.salespersons),
    team_leaders: merge(data.team_leaders),
    regional_managers: merge(data.regional_managers),
    field_staff: merge(data.field_staff),
  };
};
const STATUS_STYLE = {
  live: { badge: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500 animate-pulse" },
  recent: { badge: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  no_gps: { badge: "bg-slate-200 text-slate-700", dot: "bg-slate-400" },
  signal_gap: { badge: "bg-rose-100 text-rose-800", dot: "bg-rose-500" },
  punched_out: { badge: "bg-slate-100 text-slate-600", dot: "bg-slate-300" },
};
function StatusBadge({ person }) {
  if (!person?.status) return null;
  const style = STATUS_STYLE[person.status] || STATUS_STYLE.punched_out;
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${person.status === "no_gps" ? "min-w-0 text-right" : "whitespace-nowrap"} ${style.badge}`} data-testid="live-status">
      {person.status_label}
    </span>
  );
}

// India centre default
const INDIA_CENTER = [22.5, 79.5];

// Distinct colors for multi-day route comparison (index 0 = most recent day)
const DAY_COLORS = ["#e11d48", "#2563eb", "#059669", "#d97706", "#7c3aed", "#0891b2", "#db2777"];

// Helper — recentre the map when a salesperson is picked
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && target[0] && target[1]) {
      map.flyTo(target, Math.max(map.getZoom(), 12), { duration: 0.6 });
    }
  }, [target, map]);
  return null;
}

// Smoothly animates a marker from its previous position to the new one so the
// live dot appears to "move" across the map instead of jumping.
function AnimatedMarker({ position, icon, eventHandlers, children }) {
  const [pos, setPos] = useState(position);
  const fromRef = useRef(position);
  const rafRef = useRef();
  const lat = position?.[0];
  const lng = position?.[1];
  useEffect(() => {
    if (lat == null || lng == null) return;
    const from = fromRef.current || [lat, lng];
    const to = [lat, lng];
    if (from[0] === to[0] && from[1] === to[1]) return undefined;
    const dur = 1200;
    const t0 = performance.now();
    cancelAnimationFrame(rafRef.current);
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      const now = [from[0] + (to[0] - from[0]) * k, from[1] + (to[1] - from[1]) * k];
      fromRef.current = now; // an interrupted move continues from where the marker is
      setPos(now);
      if (k < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [lat, lng]);
  if (!pos) return null;
  return <Marker position={pos} icon={icon} eventHandlers={eventHandlers}>{children}</Marker>;
}

// ============================================================================
// Owner / TL / RM — Live Tracking Map
// ============================================================================
export function LiveTrackingPage() {
  const { user } = useAuth();
  const [live, setLive] = useState({ salespersons: [], distributors: [], retailers: [], team_leaders: [], regional_managers: [], field_staff: [] });
  const [selectedSp, setSelectedSp] = useState(null);
  const [activeList, setActiveList] = useState("salespersons"); // which legend category is expanded
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [detail, setDetail] = useState(null); // { punch, route, distance_km, visited, working_hours }
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flyTarget, setFlyTarget] = useState(null);
  const [playIdx, setPlayIdx] = useState(0);   // route playback cursor
  const [playing, setPlaying] = useState(false);
  const [multiDay, setMultiDay] = useState(false);   // compare last 7 days
  const [routes, setRoutes] = useState([]);          // [{date, points, distance_km, in_at, out_at}]
  const [hiddenDays, setHiddenDays] = useState({});  // { date: true } => hidden
  const pollBusyRef = useRef(false);
  const detailRef = useRef(null);
  detailRef.current = detail;
  const selectedRef = useRef(null);
  selectedRef.current = selectedSp;

  // Replace the route, keeping the playback cursor on the newest point if it was already there.
  const showDetail = useCallback((next) => {
    const oldLen = detailRef.current?.route?.length || 0;
    const newLen = next.route?.length || 0;
    detailRef.current = next;
    setDetail(next);
    setPlayIdx((i) => (i >= oldLen - 1 ? Math.max(0, newLen - 1) : i));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([dms.trackingLive(), dms.trackingLiveUpdates({})]);
      setLive(applyPeople(d, u.people));
    } catch (e) {
      console.warn("tracking/live failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Light poll: moves markers and extends today's route without touching the map view.
  const poll = useCallback(async () => {
    if (pollBusyRef.current || document.visibilityState !== "visible") return;
    pollBusyRef.current = true;
    try {
      const sid = selectedRef.current;
      const cur = detailRef.current;
      const follow = sid && cur?.salesperson?.id === sid && cur.date === utcToday();
      const params = follow ? { sid, since: cur.route?.[cur.route.length - 1]?.created_at } : {};
      const u = await dms.trackingLiveUpdates(params);
      setLive((prev) => applyPeople(prev, u.people));
      const latest = detailRef.current;
      if (follow && u.route?.length && latest?.salesperson?.id === sid && latest.date === cur.date) {
        const last = latest.route?.[latest.route.length - 1]?.created_at || "";
        const fresh = u.route.filter(p => p.created_at > last);
        if (fresh.length) showDetail({ ...latest, route: [...(latest.route || []), ...fresh] });
      }
    } catch (e) {
      console.warn("tracking/live-updates failed", e);
    } finally {
      pollBusyRef.current = false;
    }
  }, [showDetail]);

  useEffect(() => {
    refresh();
    const pollTimer = setInterval(poll, POLL_MS);
    const fullTimer = setInterval(refresh, FULL_REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(pollTimer);
      clearInterval(fullTimer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, poll]);

  // The selected person's km, hours and visits are heavier, so they update less often.
  useEffect(() => {
    if (!selectedSp || selectedDate !== utcToday()) return undefined;
    const t = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const d = await dms.trackingSalesperson(selectedSp, selectedDate);
        if (selectedRef.current === selectedSp) showDetail({ ...d, route: goodPoints(d.route) });
      } catch (e) {
        console.warn("tracking detail refresh failed", e);
      }
    }, DETAIL_REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedSp, selectedDate, showDetail]);

  // Load salesperson detail on select / date change
  useEffect(() => {
    if (!selectedSp) { setDetail(null); setHistory([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const [d, h] = await Promise.all([
          dms.trackingSalesperson(selectedSp, selectedDate),
          dms.trackingHistory(selectedSp, 30),
        ]);
        if (cancelled) return;
        const clean = { ...d, route: goodPoints(d?.route) };
        detailRef.current = clean;
        setDetail(clean);
        setHistory(h.data || []);
        setPlayIdx(Math.max(0, (clean.route.length || 1) - 1)); // default: show full route
        setPlaying(false);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load route");
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSp, selectedDate]);

  // Route playback auto-advance
  useEffect(() => {
    if (!playing) return;
    const n = detail?.route?.length || 0;
    if (n < 2) { setPlaying(false); return; }
    const t = setInterval(() => {
      setPlayIdx((i) => {
        if (i >= n - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, 700);
    return () => clearInterval(t);
  }, [playing, detail]);

  // Multi-day: fetch last 7 days of routes when enabled / salesperson changes
  useEffect(() => {
    if (!multiDay || !selectedSp) { setRoutes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await dms.trackingRoutes(selectedSp, 7);
        if (!cancelled) { setRoutes(r.data || []); setHiddenDays({}); }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Failed to load 7-day routes");
      }
    })();
    return () => { cancelled = true; };
  }, [multiDay, selectedSp]);

  const selectedSpMeta = useMemo(
    () => [...(live.salespersons || []), ...(live.team_leaders || []), ...(live.regional_managers || [])].find(s => s.id === selectedSp),
    [live.salespersons, live.team_leaders, live.regional_managers, selectedSp],
  );

  const fullPolyline = useMemo(() => {
    if (!detail?.route?.length) return [];
    return detail.route.map(p => [p.lat, p.lng]);
  }, [detail]);

  const routeLen = detail?.route?.length || 0;
  const playedPolyline = useMemo(() => {
    if (!detail?.route?.length) return [];
    return detail.route.slice(0, playIdx + 1).map(p => [p.lat, p.lng]);
  }, [detail, playIdx]);
  const playPoint = detail?.route?.[playIdx] || null;
  const isPartial = routeLen > 1 && playIdx < routeLen - 1;

  const routeSplitIdx = useMemo(() => {
    if (!detail?.route?.length || detail.route.length < 3) return -1;
    const start = detail.route[0];
    let maxD = -1, idx = 0;
    detail.route.forEach((p, i) => {
      const dLat = p.lat - start.lat, dLng = p.lng - start.lng;
      const d = dLat * dLat + dLng * dLng;
      if (d > maxD) { maxD = d; idx = i; }
    });
    return idx;
  }, [detail]);
  const hasReturnLeg = routeSplitIdx > 0 && routeSplitIdx < routeLen - 1;

  const upFullPolyline = useMemo(() => {
    if (!hasReturnLeg) return [];
    return fullPolyline.slice(0, routeSplitIdx + 1);
  }, [fullPolyline, hasReturnLeg, routeSplitIdx]);
  const downFullPolyline = useMemo(() => {
    if (!hasReturnLeg) return [];
    return fullPolyline.slice(routeSplitIdx);
  }, [fullPolyline, hasReturnLeg, routeSplitIdx]);
  const upPlayedPolyline = useMemo(() => {
    if (!hasReturnLeg) return [];
    return fullPolyline.slice(0, Math.min(playIdx, routeSplitIdx) + 1);
  }, [fullPolyline, hasReturnLeg, routeSplitIdx, playIdx]);
  const downPlayedPolyline = useMemo(() => {
    if (!hasReturnLeg || playIdx <= routeSplitIdx) return [];
    return fullPolyline.slice(routeSplitIdx, playIdx + 1);
  }, [fullPolyline, hasReturnLeg, routeSplitIdx, playIdx]);

  const onSelectSp = (sp) => {
    setSelectedSp(sp.id);
    if (sp.lat && sp.lng) setFlyTarget([sp.lat, sp.lng]);
  };
  const flyToItem = (item) => {
    if (item.lat && item.lng) setFlyTarget([item.lat, item.lng]);
  };

  // Legend categories — clicking a legend item expands its directory list
  const CATS = {
    salespersons: { label: "Salespersons", color: "#e11d48", selectable: true },
    team_leaders: { label: "Team Leaders", color: "#6366f1", selectable: true },
    regional_managers: { label: "Regional Managers", color: "#7c3aed", selectable: true },
    distributors: { label: "Distributors", color: "#0f766e", selectable: false },
    retailers:    { label: "Retailers",    color: "#f59e0b", selectable: false },
    field_staff:  { label: "On-duty staff", color: "#10b981", selectable: false },
  };
  const activeCat = CATS[activeList] || CATS.salespersons;
  const activeItems = live[activeList] || [];

  return (
    <div>
      <PageHeader
        title="Live Tracking"
        subtitle={
          user?.role === "team_leader"
            ? "Your team on the field — live positions and daily routes"
            : user?.role === "regional_manager"
              ? "Regional field activity — live positions and daily routes"
              : "Real-time positions of salespersons, distributors and retailers"
        }
        action={
          <Button variant="outline" onClick={refresh} data-testid="refresh-live">
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
        }
      />

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        {/* Left side — salespersons list + selected detail */}
        <div className="space-y-4">
          <Card className="p-3">
            <div className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
              <span style={{ background: activeCat.color, transform: "rotate(-45deg)" }}
                    className="h-3.5 w-3.5 rounded-full rounded-bl-none border border-white shadow inline-block" />
              <span className="text-slate-700">{activeCat.label} ({activeItems.length})</span>
            </div>
            {activeItems.length === 0 && (
              <div className="text-xs text-slate-400 py-6 text-center">None visible</div>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {activeItems.map(it => (
                <button
                  key={it.id}
                  onClick={() => (activeCat.selectable ? onSelectSp(it) : flyToItem(it))}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${activeCat.selectable && selectedSp === it.id ? "bg-[#faf6e6] border border-[#c9a227]" : "hover:bg-slate-50 border border-transparent"}`}
                  data-testid={`dir-item-${it.id}`}
                >
                  {activeCat.selectable ? (
                    <span className={`h-2 w-2 rounded-full shrink-0 ${(STATUS_STYLE[it.status] || STATUS_STYLE.punched_out).dot}`} />
                  ) : (
                    <span className="h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: activeCat.color }}>
                      {initialOf(it.name)}
                    </span>
                  )}
                  <span className="flex-1 truncate">{it.name}</span>
                  {activeCat.selectable && <StatusBadge person={it} />}
                  {it.lat != null && <MapPin size={12} className="text-slate-400" />}
                </button>
              ))}
            </div>
          </Card>

          {selectedSpMeta && (
            <Card className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{selectedSpMeta.name}</div>
                  <div className="text-xs text-slate-500">{selectedSpMeta.phone}</div>
                </div>
                <StatusBadge person={selectedSpMeta} />
              </div>

              {/* Date pick */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Route for</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    data-testid="route-date"
                  />
                  <Select value={selectedDate} onValueChange={setSelectedDate}>
                    <SelectTrigger className="w-28"><SelectValue placeholder="Quick" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={new Date().toISOString().slice(0,10)}>Today</SelectItem>
                      <SelectItem value={new Date(Date.now() - 86400000).toISOString().slice(0,10)}>Yesterday</SelectItem>
                      <SelectItem value={new Date(Date.now() - 7*86400000).toISOString().slice(0,10)}>7 days ago</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {detail && (
                <div className="space-y-2 text-sm">
                  <StatRow icon={LogIn}  label="Punch In"    value={detail.punch?.in_at ? niceDate(detail.punch.in_at) : "—"} />
                  <StatRow icon={LogOut} label="Punch Out"   value={detail.punch?.out_at ? niceDate(detail.punch.out_at) : "—"} />
                  <StatRow icon={Clock}  label="Working Hrs" value={`${detail.working_hours ?? 0} h`} />
                  <StatRow icon={RouteIcon} label="Distance" value={`${detail.distance_km ?? 0} km`} />
                  <StatRow icon={MapPin} label="Visits (Dist/Retailer)" value={`${detail.visited?.distributors?.length || 0} / ${detail.visited?.retailers?.length || 0}`} />
                  <div className="text-[11px] text-slate-500 pt-1">
                    {detail.route?.length || 0} location pings recorded
                  </div>

                  {/* Signal gaps — stretches with no GPS update while punched in; attendance is not affected */}
                  {detail.location_gaps?.length > 0 && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-1.5 mt-1">
                      <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold flex items-center gap-1">
                        <AlertTriangle size={12} /> Signal Gaps (no ping)
                      </div>
                      {detail.location_gaps.map((g, idx) => (
                        <LocationGapRow key={idx} gap={g} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Route Playback — scrub through the day's movement */}
              {routeLen > 1 && (
                <div className="rounded-lg border border-[#c9a227]/30 bg-[#faf8ef] p-3" data-testid="route-playback">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-[#8a6600] font-semibold flex items-center gap-1">
                      <RouteIcon size={12} /> Route Playback
                    </div>
                    <button
                      onClick={() => {
                        if (playIdx >= routeLen - 1) setPlayIdx(0);
                        setPlaying(p => !p);
                      }}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#c9a227] text-white hover:bg-[#a67c00]"
                      data-testid="playback-toggle"
                    >
                      {playing ? <Pause size={12} /> : <Play size={12} />}
                      {playing ? "Pause" : "Play"}
                    </button>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={routeLen - 1}
                    value={playIdx}
                    onChange={e => { setPlaying(false); setPlayIdx(Number(e.target.value)); }}
                    className="w-full accent-[#c9a227]"
                    data-testid="playback-slider"
                  />
                  <div className="flex items-center justify-between text-[11px] text-slate-600 mt-1">
                    <span>Point {playIdx + 1} / {routeLen}</span>
                    <span className="font-medium text-slate-800">
                      {playPoint?.created_at ? niceDate(playPoint.created_at) : "—"}
                    </span>
                  </div>
                </div>
              )}

              {/* Multi-Day Route Comparison (last 7 days) */}
              <div className="rounded-lg border border-slate-200 p-3">
                <label className="flex items-center justify-between cursor-pointer select-none">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                    <RouteIcon size={12} /> Compare Last 7 Days
                  </span>
                  <input
                    type="checkbox"
                    checked={multiDay}
                    onChange={e => setMultiDay(e.target.checked)}
                    data-testid="multiday-toggle"
                    className="h-5 w-9 appearance-none rounded-full bg-slate-300 checked:bg-[#c9a227] relative cursor-pointer transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-4"
                  />
                </label>
                {multiDay && (
                  <div className="mt-2 space-y-1 max-h-44 overflow-y-auto" data-testid="multiday-list">
                    {routes.length === 0 && (
                      <div className="text-[11px] text-slate-400 py-2 text-center">No route data in last 7 days</div>
                    )}
                    {routes.map((r, i) => (
                      <label key={r.date} className="flex items-center gap-2 text-xs px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!hiddenDays[r.date]}
                          onChange={() => setHiddenDays(h => ({ ...h, [r.date]: !h[r.date] }))}
                          className="accent-[#c9a227]"
                        />
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: DAY_COLORS[i % DAY_COLORS.length] }} />
                        <span className="flex-1 truncate">{r.date}{i === 0 ? " (today)" : ""}</span>
                        <span className="text-slate-500">{r.distance_km} km · {r.points?.length || 0}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {history.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 mt-2">Recent 30 days</div>
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {history.map(h => (
                      <button
                        key={h.date}
                        onClick={() => setSelectedDate(h.date)}
                        className={`w-full text-left flex items-center justify-between px-2 py-1 rounded ${h.date === selectedDate ? "bg-[#faf6e6] text-[#8a6600]" : "hover:bg-slate-50"}`}
                      >
                        <span className="flex items-center gap-1.5"><Calendar size={10} /> {h.date}</span>
                        <span className="text-slate-500">{h.working_hours} h · {h.pings} pings</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Legend — click a category to list its members */}
          <Card className="p-3">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Legend — tap to view</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <LegendBadge color="#e11d48" glyph="S" label={`Salespersons (${live.salespersons.length})`}
                active={activeList === "salespersons"} onClick={() => setActiveList("salespersons")} />
              <LegendBadge color="#6366f1" glyph="T" label={`Team Leaders (${(live.team_leaders || []).length})`}
                active={activeList === "team_leaders"} onClick={() => setActiveList("team_leaders")} />
              <LegendBadge color="#7c3aed" glyph="M" label={`Regional Managers (${(live.regional_managers || []).length})`}
                active={activeList === "regional_managers"} onClick={() => setActiveList("regional_managers")} />
              <LegendBadge color="#0f766e" glyph="D" label={`Distributors (${live.distributors.length})`}
                active={activeList === "distributors"} onClick={() => setActiveList("distributors")} />
              <LegendBadge color="#f59e0b" glyph="R" label={`Retailers (${live.retailers.length})`}
                active={activeList === "retailers"} onClick={() => setActiveList("retailers")} />
              <LegendBadge color="#10b981" glyph="●" label={`On-duty staff (${(live.field_staff || []).filter(f => f.online).length})`}
                active={activeList === "field_staff"} onClick={() => setActiveList("field_staff")} />
            </div>
          </Card>
        </div>

        {/* Right side — map */}
        <Card className="overflow-hidden" style={{ height: "calc(100vh - 220px)", minHeight: 500 }}>
          {loading && (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">Loading map…</div>
          )}
          {!loading && (
            <MapContainer center={INDIA_CENTER} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FlyTo target={flyTarget} />

              {/* Distributors */}
              {live.distributors.filter(d => d.lat && d.lng).map(d => (
                <Marker key={`d-${d.id}`} position={[d.lat, d.lng]} icon={ICON_DIST}>
                  <Popup>
                    <b>{d.name}</b><br/>
                    <span className="text-xs">Distributor · {d.region}</span><br/>
                    <span className="text-xs">{d.address}</span>
                  </Popup>
                </Marker>
              ))}

              {/* Retailers */}
              {live.retailers.filter(r => r.lat && r.lng).map(r => (
                <Marker key={`r-${r.id}`} position={[r.lat, r.lng]} icon={ICON_RET}>
                  <Popup>
                    <b>{r.name}</b><br/>
                    <span className="text-xs">Retailer</span><br/>
                    <span className="text-xs">{r.address}</span>
                  </Popup>
                </Marker>
              ))}

              {/* Salespersons — live positions (animated / moving marker with name initial) */}
              {live.salespersons.filter(s => s.lat && s.lng).map(s => (
                <AnimatedMarker key={`s-${s.id}`} position={[s.lat, s.lng]} icon={makeInitialIcon(s.online ? "#e11d48" : "#64748b", s.name)}
                        eventHandlers={{ click: () => onSelectSp(s) }}>
                  <Popup>
                    <b>{s.name}</b><br/>
                    <span className="text-xs">Salesperson</span><br/>
                    <StatusBadge person={s} />
                    {s.last_ping_at && <><br/><span className="text-[11px] text-slate-500">Last: {niceDate(s.last_ping_at)}</span></>}
                  </Popup>
                </AnimatedMarker>
              ))}

              {/* Team Leaders — live positions with name initial (selectable → route) */}
              {(live.team_leaders || []).filter(t => t.lat && t.lng).map(t => (
                <AnimatedMarker key={`t-${t.id}`} position={[t.lat, t.lng]} icon={makeInitialIcon(t.online ? "#6366f1" : "#94a3b8", t.name)}
                        eventHandlers={{ click: () => onSelectSp(t) }}>
                  <Popup>
                    <b>{t.name}</b><br/>
                    <span className="text-xs">Team Leader</span><br/>
                    <StatusBadge person={t} />
                    {t.last_ping_at && <><br/><span className="text-[11px] text-slate-500">Last: {niceDate(t.last_ping_at)}</span></>}
                  </Popup>
                </AnimatedMarker>
              ))}

              {/* Regional Managers — live positions with name initial (selectable → route) */}
              {(live.regional_managers || []).filter(m => m.lat && m.lng).map(m => (
                <AnimatedMarker key={`m-${m.id}`} position={[m.lat, m.lng]} icon={makeInitialIcon(m.online ? "#7c3aed" : "#94a3b8", m.name)}
                        eventHandlers={{ click: () => onSelectSp(m) }}>
                  <Popup>
                    <b>{m.name}</b><br/>
                    <span className="text-xs">Regional Manager</span><br/>
                    <StatusBadge person={m} />
                    {m.last_ping_at && <><br/><span className="text-[11px] text-slate-500">Last: {niceDate(m.last_ping_at)}</span></>}
                  </Popup>
                </AnimatedMarker>
              ))}

              {/* Single-day route (faint full + bold played + playback marker) */}
              {!multiDay && hasReturnLeg ? (
                <>
                  {isPartial && upFullPolyline.length > 1 && (
                    <Polyline positions={upFullPolyline} color="#2563eb" weight={2} opacity={0.25} dashArray="4 6" />
                  )}
                  {isPartial && downFullPolyline.length > 1 && (
                    <Polyline positions={downFullPolyline} color="#16a34a" weight={2} opacity={0.25} dashArray="4 6" />
                  )}
                  {upPlayedPolyline.length > 1 && (
                    <Polyline positions={upPlayedPolyline} color="#2563eb" weight={4} opacity={0.9} />
                  )}
                  {downPlayedPolyline.length > 1 && (
                    <Polyline positions={downPlayedPolyline} color="#16a34a" weight={4} opacity={0.9} />
                  )}
                </>
              ) : (
                <>
                  {fullPolyline.length > 1 && isPartial && (
                    <Polyline positions={fullPolyline} color="#e11d48" weight={2} opacity={0.25} dashArray="4 6" />
                  )}
                  {playedPolyline.length > 1 && (
                    <Polyline positions={playedPolyline} color="#e11d48" weight={4} opacity={0.9} />
                  )}
                </>
              )}
              {!multiDay && playPoint && routeLen > 1 && (
                <AnimatedMarker position={[playPoint.lat, playPoint.lng]} icon={ICON_SP}>
                  <Popup>
                    <b>{selectedSpMeta?.name || "Salesperson"}</b><br/>
                    <span className="text-xs">Position {playIdx + 1} / {routeLen}</span><br/>
                    <span className="text-[11px] text-slate-500">{playPoint.created_at ? niceDate(playPoint.created_at) : ""}</span>
                  </Popup>
                </AnimatedMarker>
              )}

              {/* Multi-day comparison — one coloured polyline per day */}
              {multiDay && routes.map((r, i) => {
                if (hiddenDays[r.date] || !(r.points?.length > 1)) return null;
                const pos = r.points.map(p => [p.lat, p.lng]);
                return (
                  <Polyline
                    key={`route-${r.date}`}
                    positions={pos}
                    color={DAY_COLORS[i % DAY_COLORS.length]}
                    weight={i === 0 ? 5 : 3}
                    opacity={i === 0 ? 0.95 : 0.7}
                  >
                    <Popup>
                      <b>{r.date}{i === 0 ? " (today)" : ""}</b><br/>
                      <span className="text-xs">{r.distance_km} km · {r.points.length} pings</span>
                    </Popup>
                  </Polyline>
                );
              })}

              {/* CONTINUATION v6: ALL other punched-in field staff (distributor / dist-accountant /
                  regional-manager / retailer) — live GPS while on duty. */}
              {(live.field_staff || [])
                .filter(f => f.lat && f.lng && !["salesperson", "team_leader"].includes(f.role))
                .map(f => (
                  <AnimatedMarker key={`fs-${f.id}`} position={[f.lat, f.lng]} icon={f.online ? ICON_SP : ICON_SP_OFF}>
                    <Popup>
                      <b>{f.name}</b><br/>
                      <span className="text-xs">{f.role_label}</span><br/>
                      <StatusBadge person={f} />
                      {f.last_ping_at && <><br/><span className="text-[11px] text-slate-500">Last: {niceDate(f.last_ping_at)}</span></>}
                    </Popup>
                  </AnimatedMarker>
                ))}
            </MapContainer>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Small UI helpers ────────────────────────────────────────────────
function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-slate-600"><Icon size={14} className="text-slate-400" /> {label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}

function LocationGapRow({ gap }) {
  const fmtLoc = (loc) => (loc && loc.lat != null && loc.lng != null)
    ? `${Number(loc.lat).toFixed(4)}, ${Number(loc.lng).toFixed(4)}`
    : "—";
  return (
    <div className="text-[11px] text-amber-900 border-t border-amber-200 first:border-t-0 pt-1.5 first:pt-0">
      <div className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
        <span className="font-semibold">Last ping</span>
        <span>{gap.off_at ? niceDate(gap.off_at) : "—"}</span>
        <span className="text-amber-700">({fmtLoc(gap.off_location)})</span>
      </div>
      <div className="flex items-center gap-1 mt-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
        <span className="font-semibold">Next ping</span>
        {gap.ongoing ? (
          <span className="text-red-700 font-semibold">Waiting</span>
        ) : (
          <>
            <span>{gap.on_at ? niceDate(gap.on_at) : "—"}</span>
            <span className="text-amber-700">({fmtLoc(gap.on_location)})</span>
          </>
        )}
      </div>
      <div className="text-slate-500 mt-0.5">Duration: {gap.duration_minutes} min</div>
    </div>
  );
}

function LegendBadge({ color, glyph, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 text-left rounded-md px-1.5 py-1 transition-colors w-full ${active ? "bg-[#faf6e6] ring-1 ring-[#c9a227]" : "hover:bg-slate-50"}`}
    >
      <div style={{ background: color, transform: "rotate(-45deg)" }}
           className="h-4 w-4 rounded-full rounded-bl-none border border-white shadow flex items-center justify-center shrink-0">
        <span style={{ transform: "rotate(45deg)", color: "white", fontSize: 8, fontWeight: 700 }}>{glyph}</span>
      </div>
      <span className={`${active ? "text-[#8a6600] font-semibold" : "text-slate-700"}`}>{label}</span>
    </button>
  );
}
